// Cross-device game-state hydration + local cache helpers (balík 2.3b).
//
// R8: for a logged-in player the SERVER (GET /user/game_state) is the source of
// truth for an in-progress game; localStorage is only a per-session cache. On
// login we pull the server state and, if it is ahead of what this device has,
// rehydrate the board from it — so a game started on the phone can be finished
// on the desktop. These helpers are pure (no React, no DOM) so the merge/mapping
// rules are unit-tested directly.

// Convert a server-stored raw similarity (0..1) to the board's 0..100 percent,
// using the SAME rounding as the live guess path (2 decimals) so a hydrated
// attempt is pixel-identical to one made live.
export function percentFromSimilarity(sim) {
  return Math.round((Number(sim) || 0) * 100 * 100) / 100;
}

// Map the server guesses list [{word, similarity, order}] to the board's attempts
// array [{guess, originalSimilarity, adjustedSimilarity}], sorted hottest-first
// (same sort as the live path). Deduplicates by word (a retried identical guess
// is harmless — keep the highest similarity seen for that word).
export function serverGuessesToAttempts(guesses) {
  if (!Array.isArray(guesses)) return [];
  const byWord = new Map();
  for (const g of guesses) {
    if (!g || typeof g.word !== "string") continue;
    const pct = percentFromSimilarity(g.similarity);
    const prev = byWord.get(g.word);
    if (prev === undefined || pct > prev) byWord.set(g.word, pct);
  }
  return [...byWord.entries()]
    .map(([guess, pct]) => ({ guess, originalSimilarity: pct, adjustedSimilarity: pct }))
    .sort((a, b) => b.adjustedSimilarity - a.adjustedSimilarity);
}

// The most recent guess = highest `order` (falls back to array position). Used to
// restore `lastAttempt` (the highlighted "just guessed" card) on resume.
export function lastGuessByOrder(guesses) {
  if (!Array.isArray(guesses) || guesses.length === 0) return null;
  let best = null;
  guesses.forEach((g, i) => {
    if (!g || typeof g.word !== "string") return;
    const ord = typeof g.order === "number" ? g.order : i;
    if (best === null || ord >= best.ord) best = { g, ord };
  });
  return best ? best.g : null;
}

// Build the board patch to apply from a server game_state (or null/undefined),
// given what this device already has locally. Returns null when there is nothing
// to hydrate: no/empty server state, or LOCAL is at least as far along (local is
// then fresher — server wins ties only when local is empty). This "take-max,
// server wins empty-tie" rule stops a stale device from clobbering fresher
// progress made elsewhere while still delivering cross-device resume.
export function hydrationFromState(state, localAttempts) {
  if (!state || !state.status || state.status === "not_started") return null;
  const serverAttempts = serverGuessesToAttempts(state.guesses);
  const local = Array.isArray(localAttempts) ? localAttempts : [];
  if (serverAttempts.length < local.length) return null;
  if (serverAttempts.length === local.length && local.length > 0) return null;

  const last = lastGuessByOrder(state.guesses);
  const lastAttempt = last
    ? {
        guess: last.word,
        originalSimilarity: percentFromSimilarity(last.similarity),
        adjustedSimilarity: percentFromSimilarity(last.similarity),
      }
    : serverAttempts[0] || null;

  return {
    attempts: serverAttempts,
    lastAttempt,
    isGuessed: state.status === "guessed",
    guessedWord: state.status === "guessed" ? state.word || null : null,
  };
}

// Take the solved word from a server game_state, deliberately WITHOUT the
// take-max attempt comparison hydrationFromState applies. The word is not
// progress — there is nothing to lose a race over: either the period is solved
// and the server knows the word, or it isn't and there is no word to take. A
// device that already holds every guess (so hydration declines) still needs
// this, because the word only ever reaches localStorage on the device that
// made the winning guess.
export function adoptSolvedWord(state, setWord, storageKey) {
  if (!state || state.status !== "guessed" || !state.word) return false;
  localStorage.setItem(storageKey, state.word);
  setWord(state.word);
  return true;
}

// The daily + weekly game localStorage keys. Cleared on logout so a shared device
// never bleeds one account's board into the next player's (server rehydration
// then refills for whoever logs in).
export const DAILY_GAME_KEYS = [
  "attempts",
  "lastAttempt",
  "isGuessed",
  "guessedWord",
  "guessOrder",
  "megaCtaRevealed",
  // Which solved word this browser already offered to the account it signed into
  // (see the guest-win claim). Belongs with the board it describes: a cleared
  // board has nothing left to claim.
  "guestWinClaimed",
];
export const WEEKLY_GAME_KEYS = [
  "weeklyAttempts",
  "weeklyLastAttempt",
  "weeklyIsGuessed",
  "weeklyGuessedWord",
  // Same as guestWinClaimed, for the mega board (see the guest-win claim).
  "guestMegaWinClaimed",
];

export function clearGameCache(storage) {
  const s = storage || (typeof localStorage !== "undefined" ? localStorage : null);
  if (!s) return;
  [...DAILY_GAME_KEYS, ...WEEKLY_GAME_KEYS].forEach((k) => s.removeItem(k));
}
